import { maskPhone } from '@oneapp/shared';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate } from 'react-router';
import { useAuth } from '../auth/AuthProvider';
import { RECAPTCHA_CONTAINER_ID, SIGN_IN_ERROR_KEYS } from '../auth/messages';
import { pauseSignIn, RESEND_AFTER_SEC, signInFlow, signInPausedUntil } from '../auth/signInFlow';
import { OtpInput, Page, PageTitle } from '../components/forms';
import { Button, Callout } from '../components/ui';
import { SignInError, sendSignInCode, toSignInError } from '../lib/firebase';
import { currentLanguage } from '../lib/i18n';
import { SignInPaused } from './SignInPaused';

type Notice = { tone: 'error' | 'amber' | 'green'; text: string };

const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/** Ticks once a second while `active`, for the resend countdown. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

/** A3 · Verify the code, with O1 (wrong code), O2 (expired: a new one is sent) and O3 (too many tries). */
export function VerifyCode() {
  if (!signInFlow.get()) return <Navigate to="/sign-in" replace />;
  return <VerifyCodeForm />;
}

function VerifyCodeForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const noticeId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [phoneE164] = useState(() => signInFlow.get()?.phoneE164 ?? '');
  const [code, setCode] = useState('');
  const [notice, setNotice] = useState<Notice>();
  const [exhausted, setExhausted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [sentAt, setSentAt] = useState(() => signInFlow.get()?.sentAt ?? Date.now());
  const [pausedUntil, setPausedUntil] = useState(() => signInPausedUntil());
  const counting = Date.now() - sentAt < RESEND_AFTER_SEC * 1000;
  const now = useNow(counting);
  const secondsLeft = Math.max(0, RESEND_AFTER_SEC - Math.floor((now - sentAt) / 1000));

  useEffect(() => {
    input.current?.focus();
  }, []);

  const backToStart = () => {
    signInFlow.clear();
    navigate('/welcome');
  };

  if (pausedUntil) return <SignInPaused until={pausedUntil} onBack={backToStart} />;

  const resend = async (expiredNotice?: string) => {
    setResending(true);
    try {
      const confirmation = await sendSignInCode(phoneE164, RECAPTCHA_CONTAINER_ID, currentLanguage());
      const pending = signInFlow.start(phoneE164, confirmation);
      setSentAt(pending.sentAt);
      setExhausted(false);
      setCode('');
      setNotice(
        expiredNotice
          ? { tone: 'amber', text: expiredNotice }
          : { tone: 'green', text: t('code.resent', { phone: maskPhone(phoneE164) }) },
      );
      input.current?.focus();
    } catch (error) {
      const problem = toSignInError(error);
      if (problem.code === 'too-many-requests') setPausedUntil(pauseSignIn());
      else setNotice({ tone: 'error', text: t(SIGN_IN_ERROR_KEYS[problem.code]) });
    } finally {
      setResending(false);
    }
  };

  const verify = async (value: string) => {
    const pending = signInFlow.get();
    if (busy || exhausted || !pending) return;
    if (value.length < 6) {
      setNotice({ tone: 'error', text: t('code.incomplete') });
      input.current?.focus();
      return;
    }
    setBusy(true);
    setNotice(undefined);
    try {
      // On success the app moves on by itself: to the profile step, or the chat.
      await auth.confirmCode(pending.confirmation, value);
    } catch (error) {
      if (!(error instanceof SignInError)) {
        setNotice({ tone: 'error', text: t('errors.generic') });
      } else if (error.code === 'wrong-code') {
        const left = signInFlow.recordWrongCode();
        setCode('');
        if (left > 0) setNotice({ tone: 'error', text: t('code.wrong', { count: left }) });
        else {
          setExhausted(true);
          setNotice({ tone: 'error', text: t('code.wrongLast') });
        }
        input.current?.focus();
      } else if (error.code === 'code-expired') {
        await resend(t('code.expired'));
      } else if (error.code === 'too-many-requests') {
        setPausedUntil(pauseSignIn());
      } else {
        setNotice({ tone: 'error', text: t(SIGN_IN_ERROR_KEYS[error.code]) });
      }
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void verify(code);
  };

  return (
    <Page
      bar={t('signIn.bar')}
      back={{ label: t('signIn.back'), onClick: () => navigate('/sign-in') }}
      labelledBy="code-title"
      footer={
        <Button
          type="submit"
          form="code-form"
          busy={busy}
          busyLabel={t('code.checking')}
          disabled={exhausted || resending}
        >
          {t('code.verify')}
        </Button>
      }
    >
      <PageTitle id="code-title" title={t('code.title')}>
        {t('code.sentTo', { phone: maskPhone(phoneE164, { showStart: true }) })}{' '}
        <Link to="/sign-in" className="strong-link" onClick={() => signInFlow.clear()}>
          {t('code.changeNumber')}
        </Link>
      </PageTitle>
      <form id="code-form" onSubmit={onSubmit} noValidate>
        <OtpInput
          value={code}
          onChange={(next) => {
            setCode(next);
            if (notice?.tone === 'error' && !exhausted) setNotice(undefined);
          }}
          onComplete={(full) => void verify(full)}
          label={t('code.codeLabel')}
          error={notice?.tone === 'error'}
          describedBy={notice ? noticeId : undefined}
          disabled={busy || exhausted}
          inputRef={input}
        />
      </form>
      {notice ? (
        <div id={noticeId}>
          <Callout
            tone={notice.tone}
            icon={notice.tone === 'amber' ? 'clock' : notice.tone === 'green' ? 'check-circle' : 'alert-triangle'}
            role={notice.tone === 'error' ? 'alert' : 'status'}
          >
            {notice.text}
          </Callout>
        </div>
      ) : null}
      <div className="resend">
        <span>{t('code.didntGet')}</span>
        {secondsLeft > 0 && !exhausted ? (
          <span className="resend__timer" aria-live="off">
            {t('code.resendIn', { time: clock(secondsLeft) })}
          </span>
        ) : (
          <Button variant="ghost" inline small busy={resending} onClick={() => void resend()}>
            {t('code.resend')}
          </Button>
        )}
      </div>
      <Callout icon="shield-check">{t('code.autofill')}</Callout>
      <div id={RECAPTCHA_CONTAINER_ID} />
    </Page>
  );
}
